import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineRefresh, HiOutlineCheckCircle, HiOutlineXCircle,
  HiOutlineLightningBolt, HiOutlineSearch,
  HiOutlineChevronDown, HiOutlineChevronRight,
  HiOutlineExternalLink, HiOutlineDownload,
} from 'react-icons/hi';
import { getCloudBuildTriggers, getServiceTags, setServiceTag } from '../services/api';
import ServiceTagDropdown, { groupAndSort, getTagLabel, TagFilter, stickyTh, stickyTd, stickyGroupTd } from '../components/ServiceTagDropdown';
import './CloudScheduler.css';

const SERVICE_TYPE = 'cloud-build';
const BUILD_COST_PER_MIN_INR = 0.27;

function estimateBuildCost(t) {
  if (t.disabled) return 0;
  if (!t.lastBuild?.duration) return null;
  const buildsPerMonth = 30;
  const minutesPerBuild = t.lastBuild.duration / 60;
  return Math.round(minutesPerBuild * buildsPerMonth * BUILD_COST_PER_MIN_INR * 100) / 100;
}

function formatCost(cost) {
  if (cost === null || cost === undefined) return '—';
  if (cost === 0) return <span className="cost-cell"><span className="cost-approx">~</span> ₹0</span>;
  return <span className="cost-cell"><span className="cost-approx">~</span> ₹{cost.toLocaleString('en-IN')}</span>;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function formatDuration(seconds) {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function buildStatusBadge(statusText) {
  if (!statusText || statusText === 'Unknown') return <span className="status-badge neutral">Unknown</span>;
  if (statusText === 'Success') return <span className="status-badge success"><HiOutlineCheckCircle size={12} /> Success</span>;
  if (['Failed', 'Internal Error', 'Timeout'].includes(statusText)) return <span className="status-badge error"><HiOutlineXCircle size={12} /> {statusText}</span>;
  if (['Building', 'Queued', 'Pending'].includes(statusText)) return <span className="status-badge warning">{statusText}</span>;
  if (statusText === 'Cancelled') return <span className="status-badge neutral">Cancelled</span>;
  return <span className="status-badge neutral">{statusText}</span>;
}

function escapeCsvField(val) {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function csvHyperlink(url, label) {
  if (!url) return escapeCsvField(label || '');
  const safeUrl = url.replace(/"/g, '""');
  const safeLabel = String(label || url).replace(/"/g, '""');
  return `"=HYPERLINK(""${safeUrl}"",""${safeLabel}"")"`;
}

function cleanBranch(val) {
  if (!val || val === '—') return val;
  return val.replace(/^\^/, '').replace(/\$$/, '');
}

function CloudBuild({ gcpProjects, selectedProjectId }) {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [sortField, setSortField] = useState('repo');
  const [sortDir, setSortDir] = useState('asc');
  const [tagMap, setTagMap] = useState({});
  const [tagFilter, setTagFilter] = useState('all');

  const activeProject = gcpProjects.find(p => p._id === selectedProjectId);

  const fetchTriggers = useCallback(async (signal) => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await getCloudBuildTriggers(activeProject._id);
      if (!signal?.cancelled) setTriggers(res.data || []);
    } catch (err) {
      if (!signal?.cancelled) {
        toast.error('Failed to load Cloud Build triggers: ' + (err.response?.data?.error || err.message));
        setTriggers([]);
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [activeProject]);

  const fetchTags = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await getServiceTags(activeProject._id);
      const map = {};
      for (const [key, val] of Object.entries(res.data || {})) {
        if (key.startsWith(SERVICE_TYPE + ':')) {
          map[key.replace(SERVICE_TYPE + ':', '')] = val;
        }
      }
      setTagMap(map);
    } catch { /* tags are optional */ }
  }, [activeProject]);

  useEffect(() => {
    setTriggers([]);
    setExpandedRows({});
    setTagMap({});
    if (!activeProject) return;
    const signal = { cancelled: false };
    fetchTriggers(signal);
    fetchTags();
    return () => { signal.cancelled = true; };
  }, [activeProject, fetchTriggers, fetchTags]);

  const handleTagChange = async (serviceName, tag) => {
    const prev = { ...tagMap };
    setTagMap(m => tag ? { ...m, [serviceName]: tag } : (() => { const n = { ...m }; delete n[serviceName]; return n; })());
    try {
      await setServiceTag(activeProject._id, SERVICE_TYPE, serviceName, tag);
    } catch {
      setTagMap(prev);
      toast.error('Failed to update tag');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-hint">⇅</span>;
    return <span className="sort-active">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortFn = useCallback((list) => {
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortField === 'repo') return mul * (a.repoName || '').localeCompare(b.repoName || '');
      if (sortField === 'name') return mul * (a.name || '').localeCompare(b.name || '');
      if (sortField === 'event') return mul * (a.eventType || '').localeCompare(b.eventType || '');
      if (sortField === 'branch') return mul * (a.branchPattern || '').localeCompare(b.branchPattern || '');
      if (sortField === 'status') {
        const sa = a.lastBuild?.statusText || 'zzz';
        const sb = b.lastBuild?.statusText || 'zzz';
        return mul * sa.localeCompare(sb);
      }
      if (sortField === 'region') return mul * (a.region || '').localeCompare(b.region || '');
      if (sortField === 'config') return mul * (a.buildConfig || '').localeCompare(b.buildConfig || '');
      return 0;
    });
  }, [sortField, sortDir]);

  const grouped = useMemo(() => {
    let list = triggers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.eventType.toLowerCase().includes(q) ||
        (t.repoName || '').toLowerCase().includes(q) ||
        (t.buildConfig || '').toLowerCase().includes(q)
      );
    }
    return groupAndSort(list, tagMap, 'name', sortFn, tagFilter);
  }, [triggers, search, tagMap, sortFn, tagFilter]);

  const flatFiltered = useMemo(() => grouped.filter(r => !r._groupHeader), [grouped]);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  const COL_COUNT = 14;

  const downloadCSV = () => {
    if (flatFiltered.length === 0) return;
    const headers = ['Tag', 'Trigger Name', 'Repository', 'Branch', 'Build Config', 'Est. Cost (INR)', 'Event Type', 'Region', 'Enabled', 'Last Build Status', 'Last Build Time', 'Duration (s)'];
    const rows = flatFiltered.map(t => [
      escapeCsvField(getTagLabel(tagMap[t.name]) || ''),
      csvHyperlink(t.triggerUrl, t.name),
      csvHyperlink(t.repoUrl, t.repoName),
      escapeCsvField(cleanBranch(t.branchPattern)),
      escapeCsvField(t.buildConfig || ''),
      escapeCsvField(estimateBuildCost(t) ?? ''),
      escapeCsvField(t.eventType),
      escapeCsvField(t.region || 'global'),
      escapeCsvField(t.disabled ? 'Disabled' : 'Enabled'),
      escapeCsvField(t.lastBuild?.statusText || 'No builds'),
      escapeCsvField(t.lastBuild?.createTime || ''),
      escapeCsvField(t.lastBuild?.duration || ''),
    ]);

    const csvContent = [
      headers.map(escapeCsvField).join(','),
      ...rows.map(r => r.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cloud-build-triggers-${activeProject?.projectId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeProject) {
    return (
      <div className="cloud-scheduler page-container">
        <div className="gcp-page-header">
          <div className="gcp-page-title-row"><h1><HiOutlineLightningBolt size={28} /> Cloud Build</h1></div>
        </div>
        <div className="empty-state"><p>Select a GCP project to view Cloud Build triggers.</p></div>
      </div>
    );
  }

  return (
    <div className="cloud-scheduler page-container">
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineLightningBolt size={28} /> Cloud Build</h1>
          <span className="pv-subtitle">{activeProject.projectName} — {flatFiltered.length} trigger{flatFiltered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="gcp-page-toolbar">
          <div className="header-search">
            <HiOutlineSearch size={14} />
            <input type="text" placeholder="Search triggers..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <TagFilter value={tagFilter} onChange={setTagFilter} />
          <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={loading || flatFiltered.length === 0} title="Download CSV">
            <HiOutlineDownload size={14} /> CSV
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchTriggers({})} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && triggers.length === 0 && (
        <div className="empty-state"><p>No Cloud Build triggers found in <strong>{activeProject.projectName}</strong>.</p></div>
      )}

      {grouped.length > 0 && (
        <div className="resource-table-wrap">
          <div className="resource-table-scroll">
          <table className="resource-table">
            <thead>
              <tr>
                <th style={stickyTh[0]}></th>
                <th style={stickyTh[1]}>Tag</th>
                <th className="sortable-th" style={stickyTh[2]} onClick={() => handleSort('name')}>Trigger Name <SortIcon field="name" /></th>
                <th className="sortable-th" onClick={() => handleSort('event')}>Event <SortIcon field="event" /></th>
                <th className="sortable-th" onClick={() => handleSort('repo')}>Repository <SortIcon field="repo" /></th>
                <th className="sortable-th" onClick={() => handleSort('branch')}>Branch <SortIcon field="branch" /></th>
                <th className="sortable-th" onClick={() => handleSort('config')}>Build Config <SortIcon field="config" /></th>
                <th>Est. Cost</th>
                <th className="sortable-th" onClick={() => handleSort('region')}>Region <SortIcon field="region" /></th>
                <th className="sortable-th" onClick={() => handleSort('status')}>Last Build Status <SortIcon field="status" /></th>
                <th>Last Build Time</th>
                <th>Duration</th>
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((row, i) => {
                if (row._groupHeader) {
                  return (
                    <tr key={`gh-${row.group}`} className="group-header-row">
                      <td style={stickyGroupTd[0]}></td>
                      <td style={stickyGroupTd[1]}></td>
                      <td style={stickyGroupTd[2]}>
                        <span className="group-label">
                          <span className={`group-dot ${row.group}`}></span>
                          {row.label}
                          <span className="group-count">({row.count})</span>
                        </span>
                      </td>
                      <td colSpan={COL_COUNT - 3}></td>
                    </tr>
                  );
                }
                const t = row;
                const key = t.id || i;
                const isOpen = expandedRows[key];
                const cost = estimateBuildCost(t);
                return (
                  <React.Fragment key={key}>
                    <tr className="expandable-row" onClick={() => toggleRow(key)}>
                      <td className="expand-toggle" style={stickyTd[0]}>
                        {isOpen ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
                      </td>
                      <td style={stickyTd[1]}>
                        <ServiceTagDropdown value={tagMap[t.name] || ''} onChange={(tag) => handleTagChange(t.name, tag)} />
                      </td>
                      <td className="cell-name" style={stickyTd[2]}>
                        {t.triggerUrl ? (
                          <a href={t.triggerUrl} target="_blank" rel="noopener noreferrer" className="trigger-link" onClick={e => e.stopPropagation()}>
                            {t.name} <HiOutlineExternalLink size={11} />
                          </a>
                        ) : t.name}
                      </td>
                      <td><span className="region-badge">{t.eventType}</span></td>
                      <td>
                        {t.repoUrl ? (
                          <a href={t.repoUrl} target="_blank" rel="noopener noreferrer" className="repo-link" onClick={e => e.stopPropagation()}>
                            <code>{t.repoName}</code> <HiOutlineExternalLink size={11} />
                          </a>
                        ) : (t.repoName !== '—' ? <code>{t.repoName}</code> : '—')}
                      </td>
                      <td>{t.branchPattern !== '—' ? <code>{cleanBranch(t.branchPattern)}</code> : '—'}</td>
                      <td><code>{t.buildConfig || '—'}</code></td>
                      <td>{formatCost(cost)}</td>
                      <td><span className="region-badge">{t.region || 'global'}</span></td>
                      <td>{t.lastBuild ? buildStatusBadge(t.lastBuild.statusText) : <span className="status-badge neutral">No builds</span>}</td>
                      <td>{formatTimestamp(t.lastBuild?.createTime)}</td>
                      <td>{formatDuration(t.lastBuild?.duration)}</td>
                      <td>
                        <span className={`status-badge ${t.disabled ? 'error' : 'success'}`}>
                          {t.disabled ? 'Disabled' : 'Enabled'}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="expand-detail-row">
                        <td colSpan={COL_COUNT}>
                          <div className="expand-detail">
                            <div className="detail-grid">
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Trigger ID</span>
                                  <span className="detail-value mono">{t.id}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Event Type</span>
                                  <span className="detail-value">{t.eventType}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Region</span>
                                  <span className="detail-value">{t.region || 'global'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Created</span>
                                  <span className="detail-value">{formatTimestamp(t.createTime)}</span>
                                </div>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Repository</span>
                                  <span className="detail-value mono">
                                    {t.repoUrl ? (
                                      <a href={t.repoUrl} target="_blank" rel="noopener noreferrer" className="repo-link">
                                        {t.repoName} <HiOutlineExternalLink size={11} />
                                      </a>
                                    ) : t.repoName}
                                  </span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Branch / Tag</span>
                                  <span className="detail-value mono">{cleanBranch(t.branchPattern)}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Build Config</span>
                                  <span className="detail-value mono">{t.buildConfig || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Trigger Link</span>
                                  <span className="detail-value">
                                    {t.triggerUrl ? (
                                      <a href={t.triggerUrl} target="_blank" rel="noopener noreferrer" className="trigger-link">
                                        Open in GCP Console <HiOutlineExternalLink size={11} />
                                      </a>
                                    ) : '—'}
                                  </span>
                                </div>
                              </div>
                              {t.description && (
                                <div className="detail-item full-width">
                                  <span className="detail-label">Description</span>
                                  <span className="detail-value">{t.description}</span>
                                </div>
                              )}
                              {t.lastBuild && (
                                <>
                                  <div className="detail-divider">Last Build</div>
                                  <div className="detail-row">
                                    <div className="detail-item">
                                      <span className="detail-label">Build ID</span>
                                      <span className="detail-value mono">{t.lastBuild.id}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Started</span>
                                      <span className="detail-value">{formatTimestamp(t.lastBuild.createTime)}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Finished</span>
                                      <span className="detail-value">{formatTimestamp(t.lastBuild.finishTime)}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Duration</span>
                                      <span className="detail-value">{formatDuration(t.lastBuild.duration)}</span>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!loading && triggers.length > 0 && flatFiltered.length === 0 && (
        <div className="empty-state"><p>No triggers match "<strong>{search}</strong>"</p></div>
      )}
    </div>
  );
}

export default CloudBuild;
